/**
 * Keyboard overlap — how much of the content the on-screen keyboard actually hides.
 *
 * A screen that lifts, pads or scrolls for the keyboard needs the height the
 * keyboard COVERS, which is not the height the plugin reports. There are THREE window
 * behaviours, and they need three different answers. All figures below were measured on
 * device on 2026-09-06 — see docs/investigations/2026-09-06-keyboard-overlap-measurement.md.
 *
 *   RESIZE — the layout viewport shrinks, so the room already exists and the offset is
 *     0. Android with a resizing window (API 33: innerHeight 802 -> 514). `resize` is an
 *     iOS-only config key — the Android half of `@capacitor/keyboard` never reads it
 *     (`load()` takes only `resizeOnFullScreen`) — and the shrink comes from
 *     `android:windowSoftInputMode`, which AndroidManifest.xml does not set, so the
 *     system default applies it on its own.
 *
 *   PAN — the layout viewport is untouched and the system slides the whole webview UP so
 *     the focused field clears the IME. Android 16+ (API 37: innerHeight stays 952,
 *     visualViewport.offsetTop 264, visualViewport.height 616). innerHeight, scrollY and
 *     every element rect are blind to this; `visualViewport.offsetTop` is the only signal
 *     that exposes it, and reserving the reported height on top of a pan the system had
 *     already applied put the sheet 264dp too high.
 *
 *   OVERLAY — the keyboard sits ON TOP and nothing moves, so the whole reported height
 *     has to be accounted for. iOS, via `Keyboard: { resize: "none" }`. Note iOS's
 *     visualViewport is INERT here: 956 tall with a 345pt keyboard up, offsetTop 0. It
 *     reports nothing, which is why the plugin's height is the fallback.
 *
 * Which case a device is in cannot be read off the platform: this app targets SDK 36,
 * where `windowOptOutEdgeToEdgeEnforcement` is ignored, so an Android 16 device is
 * edge-to-edge and pans rather than resizing. Same OS, opposite answers — so the
 * viewport is measured across the keyboard opening and believed.
 *
 * Note that RESIZE and OVERLAY are indistinguishable AFTER the fact (both end up with
 * offsetTop 0 and visualViewport.height === innerHeight), which is why `keyboardResized`
 * is latched at the moment the keyboard opens rather than derived later.
 *
 * Written for the Next Steps reader (`pages/courses/_lessonId.vue`, ONE-8118) and
 * lifted here when the highlight drawer needed the same measurement rather than a
 * second copy of it.
 *
 * -- What a host gets -------------------------------------------------------
 *
 *   keyboardOverlapPx   computed  px of the layout the keyboard actually covers;
 *                                 0 when it is down, and 0 on a webview that
 *                                 resized by the whole of it.
 *   keyboardActive      data      whether the keyboard is up at all — the signal
 *                                 to use for "is the reader typing", since the
 *                                 overlap is legitimately 0 while it is.
 *   keyboardOffsetPx    computed  what to offset by, in px. The overlap, with no
 *                                 guessed fallback: it is only read while the
 *                                 keyboard is up, which means the plugin IS
 *                                 reporting, so 0 is an answer and not an absence.
 *   keyboardInsetCss    computed  the same as a CSS length, falling back to the
 *                                 env() expression so a host with no plugin (the
 *                                 web build on a phone) still reserves something.
 *                                 Hosts keep their own "is a field focused" test
 *                                 around it — see the register flow.
 *   keyboardHeight      data      what the plugin reported, raw.
 *
 * -- What a host does -------------------------------------------------------
 *
 *   startKeyboardOverlapTracking()  subscribe. On mount for a screen, or on
 *                                   present for an overlay that should not
 *                                   outlive itself. Safe to call twice.
 *   stopKeyboardOverlapTracking()   unsubscribe and reset. Called automatically on
 *                                   unmount; call it directly on dismiss.
 *
 * -- What a host may override -----------------------------------------------
 *
 *   onKeyboardOverlapShown()      after each measurement (`willShow`, `didShow`
 *                                 and any resize while up). Default: nothing.
 *   onKeyboardOverlapWillHide()   as the keyboard starts sliding away. Default:
 *                                 resetKeyboardOverlap(). An override that wants
 *                                 to measure the old layout first must call
 *                                 resetKeyboardOverlap() itself.
 *   onKeyboardOverlapDidHide()    once it has gone. Default: nothing. For layout a
 *                                 host wants to undo AFTER the animation rather
 *                                 than during it — dropping reserved space early
 *                                 shortens the page while the keyboard is still
 *                                 covering it, which the scroll then clamps against.
 */
import { Keyboard } from '@capacitor/keyboard';

// How long a viewport change has to stand before it counts as the new resting
// height. A webview that resizes for the keyboard fires its resize either side of
// `keyboardWillShow`, and one that lands JUST BEFORE would otherwise be recorded as
// the resting height — the baseline would then already include the keyboard's bite,
// measure no shrink, and offset a second time. willShow cancels anything pending, so
// only a change the keyboard did not cause (a rotation, a resized window) survives.
const VIEWPORT_SETTLE_MS = 400;

// Stand-in for a keyboard nobody measured: no height reported AND no viewport
// movement, which is the web build and any platform whose plugin reports 0. A
// typical keyboard beats reserving nothing. Never reached once anything is known —
// an overlap of 0 that WAS measured means the layout already made the room.
export const KEYBOARD_FALLBACK_PX = 300;
// The same as CSS, preferring the browser's own figure where it has one. env()
// covers iOS Safari; it resolves to the fallback everywhere else.
export const KEYBOARD_FALLBACK_CSS = `max(${KEYBOARD_FALLBACK_PX}px, env(keyboard-inset-height, ${KEYBOARD_FALLBACK_PX}px))`;

export default {
  data() {
    return {
      // Whether the keyboard is up. Not a proxy for "something is covered" — see
      // keyboardOverlapPx, which is 0 on a webview that resized by the whole of it.
      keyboardActive: false,
      // Height the plugin reported. It varies a lot (suggestion strips, split and
      // floating keyboards), which is why nothing here guesses one.
      keyboardHeight: 0,
      // Viewport height the moment the keyboard began to open, and whether the
      // viewport has given ANY of it up since. Together they say whether this webview
      // shrinks for the keyboard or is covered by it.
      viewportHeightBeforeKeyboard: 0,
      // Latched for the life of one keyboard session — see measureKeyboardShrink.
      keyboardResized: false,
      // True once the plugin has delivered ANY keyboard event, and never cleared. This
      // separates the only two things the old `keyboardMeasured` conflated: "there is no
      // plugin here" (the web build, where no event ever arrives) from "the plugin is
      // talking to us and says zero" (a floating keyboard, and a transient at the start of
      // an open). Only the first wants a guessed height; guessing for the second reserved
      // 300px for a keyboard floating in the middle of the screen.
      keyboardPluginSeen: false,
      // The visual viewport: how much is actually visible, and how far the system has
      // slid the webview up. Held in DATA rather than read from window.visualViewport in
      // the computed, because those properties are not reactive AND the pan arrives late:
      // measured on API 37, offsetTop is still 0 at keyboardDidShow and animates to ~255
      // over the following ~900ms. A value read once at didShow is 0 and stays 0, so the
      // offset kept the whole reported height while the system slid the page away
      // underneath it.
      visualViewportHeight: 0,
      visualViewportPan: 0,
      visualViewportScale: 1,
      // The viewport's height with no keyboard up. The baseline cannot simply be read
      // at `willShow`: a webview that resizes may have already done so by then (the
      // resize and the event race, and which wins is not ours to decide), and a
      // baseline read after the shrink measures no shrink at all — the doubled offset
      // straight back. So it is tracked here instead, and only from viewport changes
      // that outlive VIEWPORT_SETTLE_MS without a keyboard arriving.
      restingViewportHeight: 0,
      restingViewportTimer: null,
      // Set SYNCHRONOUSLY by startKeyboardOverlapTracking, before its first await. The
      // handles below are only assigned four bridge round-trips later, so a guard that
      // reads them lets a second call through and leaks a whole set of listeners.
      keyboardTracking: false,
      // Bumped by every stop(). A subscribe whose generation is stale by the time the
      // bridge answers removes its own handles instead of installing them — otherwise a
      // sheet dismissed mid-subscribe ends up with four listeners nobody can remove AND
      // no window resize listener, since stop() already took that off.
      keyboardTrackingGen: 0,
      keyboardShowHandle: null,
      keyboardDidShowHandle: null,
      keyboardHideHandle: null,
      keyboardDidHideHandle: null,
    };
  },
  computed: {
    // How much of the layout the keyboard actually hides. Three window behaviours, asked
    // in order — see the header for the measurements behind each.
    //
    // What this deliberately does NOT do is subtract the reported height from the
    // viewport's shrink. Those two are measured from different edges: Android reports
    // the IME inset from the bottom of the SCREEN, nav bar included, while a resizing
    // window only gives up the height it actually had. On API 33 that left 336 - 288 =
    // 48 — exactly the three-button nav bar — floating every sheet off the bottom.
    keyboardOverlapPx() {
      if (!this.keyboardActive) return 0;

      // 1. The LAYOUT viewport shrank: the room already exists, so reserve nothing.
      //    Android with a resizing window (API 33: 802 -> 514).
      if (this.keyboardResized) return 0;

      // 2. The VISUAL viewport moved or narrowed under a layout viewport that did not.
      //    Covers the pan and a plain overlay alike. Android 16+ is edge-to-edge and
      //    PANS: it slides the whole webview up so the focused field clears the IME,
      //    without touching innerHeight, scrollY or any element's rect — visualViewport
      //    .offsetTop is the only signal that exposes it. Reserving the reported height
      //    on top of a pan the system already applied put the sheet 264dp too high
      //    (API 37: iH 952, offsetTop 264, vv 616 -> 72 covered, not 336).
      const visible = this.visualViewportHeight;
      const moved = visible > 0 && (visible < this.viewportHeight() || this.visualViewportPan > 0);
      if (moved && this.visualViewportScale === 1) {
        return Math.max(0, Math.round(this.viewportHeight() - this.visualViewportPan - visible));
      }

      // 3. Nothing in the browser noticed anything: iOS under `resize: "none"`, where
      //    visualViewport is inert (956 tall with a 345pt keyboard up, offsetTop 0).
      //    Trusting the plugin is safe precisely BECAUSE nothing moved or shrank.
      return this.keyboardHeight;
    },
    // What a host should offset by while the keyboard is up.
    keyboardOffsetPx() {
      if (!this.keyboardActive) return 0;
      // No fallback. This is only ever read while keyboardActive is true, which means the
      // plugin IS reporting — so an overlap of 0 is an answer, not an absence. Reserving a
      // guessed 300px here put that much dead space under a floating keyboard.
      return this.keyboardOverlapPx;
    },
    // The same as a CSS length. Deliberately NOT gated on keyboardActive: hosts that
    // also lift for a focused field with no native keyboard event behind it (the web
    // build) apply this on their own condition, and want the fallback there.
    keyboardInsetCss() {
      // Deliberately NOT gated on keyboardActive: hosts that lift for a focused field with
      // no native keyboard event behind it (the web build) apply this on their own
      // condition and want the fallback. But once the plugin has spoken even once, its
      // answer is better than a guess — including while the keyboard is sliding away,
      // where falling back mid-dismissal produced a 0 -> 300 -> 0 flash on every register
      // and profile screen, on every platform.
      return this.keyboardPluginSeen ? `${this.keyboardOverlapPx}px` : KEYBOARD_FALLBACK_CSS;
    },
  },
  unmounted() {
    this.stopKeyboardOverlapTracking();
  },
  methods: {
    // `willShow` opens the measurement (baseline) and `didShow` closes it, by which
    // point a webview that resizes has done so. Neither is trusted to be the whole
    // story: the resize can land outside both, so restingViewportHeight backs up the
    // baseline and onViewportResize re-measures.
    //
    // Deliberately not awaited past the first listener by callers that present UI:
    // this is a native bridge round-trip, and nothing can be typed before it settles.
    async startKeyboardOverlapTracking() {
      if (this.keyboardTracking) return;
      this.keyboardTracking = true;
      const generation = this.keyboardTrackingGen;
      this.restingViewportHeight = this.viewportHeight();
      window.addEventListener('resize', this.onViewportResize);
      this.measureVisualViewport();
      const vv = window.visualViewport;
      if (vv && typeof vv.addEventListener === 'function') {
        // `scroll` is the one that matters: a pan moves offsetTop without changing height.
        vv.addEventListener('resize', this.onVisualViewportChange);
        vv.addEventListener('scroll', this.onVisualViewportChange);
      }
      try {
        const showHandle = await Keyboard.addListener('keyboardWillShow', info => {
          if (!this.keyboardActive) {
            // Anything this keyboard's own resize scheduled is not a resting height.
            clearTimeout(this.restingViewportTimer);
            this.viewportHeightBeforeKeyboard = Math.max(
              this.restingViewportHeight,
              this.viewportHeight(),
            );
          }
          this.keyboardPluginSeen = true;
          this.keyboardHeight = info?.keyboardHeight || 0;
          this.keyboardActive = true;
          this.measureKeyboardShrink();
          this.measureVisualViewport();
          this.onKeyboardOverlapShown();
        });
        const didShowHandle = await Keyboard.addListener('keyboardDidShow', info => {
          this.keyboardPluginSeen = true;
          if (info?.keyboardHeight) this.keyboardHeight = info.keyboardHeight;
          this.measureKeyboardShrink();
          this.measureVisualViewport();
          this.onKeyboardOverlapShown();
        });
        // `willHide`, not `didHide`: an offset has to come off in step with the
        // keyboard sliding away, and a host that restores scroll position needs to
        // measure before the reflow rather than after the jump has happened.
        const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
          this.keyboardPluginSeen = true;
          this.onKeyboardOverlapWillHide();
        });
        const didHideHandle = await Keyboard.addListener('keyboardDidHide', () => {
          this.keyboardPluginSeen = true;
          this.onKeyboardOverlapDidHide();
        });
        // Tracking was stopped while the bridge was answering: throw the listeners away
        // rather than installing them onto a component that has already torn down.
        if (generation !== this.keyboardTrackingGen) {
          [showHandle, didShowHandle, hideHandle, didHideHandle].forEach(h => h?.remove());
          return;
        }
        this.keyboardShowHandle = showHandle;
        this.keyboardDidShowHandle = didShowHandle;
        this.keyboardHideHandle = hideHandle;
        this.keyboardDidHideHandle = didHideHandle;
      } catch (e) {
        // Keyboard plugin is unavailable on web/desktop — the overlap stays 0.
      }
    },

    stopKeyboardOverlapTracking() {
      this.keyboardTracking = false;
      this.keyboardTrackingGen += 1;
      clearTimeout(this.restingViewportTimer);
      window.removeEventListener('resize', this.onViewportResize);
      const vv = typeof window === 'undefined' ? null : window.visualViewport;
      if (vv && typeof vv.removeEventListener === 'function') {
        vv.removeEventListener('resize', this.onVisualViewportChange);
        vv.removeEventListener('scroll', this.onVisualViewportChange);
      }
      this.keyboardShowHandle?.remove();
      this.keyboardDidShowHandle?.remove();
      this.keyboardHideHandle?.remove();
      this.keyboardDidHideHandle?.remove();
      this.keyboardShowHandle = null;
      this.keyboardDidShowHandle = null;
      this.keyboardHideHandle = null;
      this.keyboardDidHideHandle = null;
      this.resetKeyboardOverlap();
    },

    // The layout viewport, which is what a full-height scrollport is sized from.
    // Deliberately NOT visualViewport: on a webview that does not resize, the visual
    // viewport can still report the keyboard's bite, and reading that would cancel
    // exactly the offset such a webview needs.
    viewportHeight() {
      return typeof window === 'undefined' ? 0 : window.innerHeight || 0;
    },

    // A viewport change is either the keyboard taking room (measure it) or the
    // resting height moving under us — a rotation, a window resize on the web. The
    // second is only believed once it has stood for VIEWPORT_SETTLE_MS, because a
    // keyboard opening can look exactly like it for the moment before `willShow`.
    onViewportResize() {
      if (this.keyboardActive) {
        this.measureKeyboardShrink();
        return;
      }
      clearTimeout(this.restingViewportTimer);
      this.restingViewportTimer = setTimeout(() => {
        if (!this.keyboardActive) this.restingViewportHeight = this.viewportHeight();
      }, VIEWPORT_SETTLE_MS);
    },

    // Did this webview give up any height to the keyboard? Runs on every signal that
    // the viewport may have changed — `didShow` and window resize — because which of
    // them lands first varies.
    //
    // LATCHED, and deliberately never cleared while the keyboard is up. On the way out
    // the window grows back BEFORE `keyboardWillHide` arrives, so a value recomputed
    // there reads "nothing was lost" while the keyboard is still on screen. Measured on
    // API 33: the viewport returned to 802 at t=3975 and willHide only landed at t=4682,
    // and in those 707ms the offset became the whole reported height — the sheet leapt
    // 336px, 42% of the screen, then dropped. resetKeyboardOverlap() clears it.
    measureKeyboardShrink() {
      if (!this.keyboardActive || !this.viewportHeightBeforeKeyboard) return;
      if (this.viewportHeightBeforeKeyboard - this.viewportHeight() > 0) {
        this.keyboardResized = true;
      }
    },

    // Snapshot the visual viewport into reactive data. Called on subscribe, on every
    // keyboard event, and from the visual viewport's own resize/scroll events — the pan
    // animates in long after keyboardDidShow and fires `scroll`, not `resize`.
    measureVisualViewport() {
      const vv = typeof window === 'undefined' ? null : window.visualViewport;
      if (!vv) return;
      this.visualViewportHeight = Math.round(vv.height) || 0;
      this.visualViewportPan = Math.round(vv.offsetTop) || 0;
      this.visualViewportScale = vv.scale || 1;
    },

    onVisualViewportChange() {
      this.measureVisualViewport();
    },

    // Back to "no keyboard". Not restingViewportHeight: the viewport is mid-animation
    // back to full when this runs, so reading it now would record a short one. The
    // resize that lands as the keyboard finishes closing sets it, via onViewportResize.
    resetKeyboardOverlap() {
      this.keyboardActive = false;
      this.keyboardHeight = 0;
      this.keyboardResized = false;
      this.viewportHeightBeforeKeyboard = 0;
    },

    // Overridable hooks — see the header.
    onKeyboardOverlapShown() {},
    onKeyboardOverlapWillHide() {
      this.resetKeyboardOverlap();
    },
    onKeyboardOverlapDidHide() {},
  },
};
